import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import MetaTags from 'components/common/MetaTags';
import MeetPageHeader from 'components/NewDesign/MeetPageHeader';
import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import MeetPointsDashboard from 'components/admin/reports/MeetPointsDashboard';
import MeetPointsHelp from 'components/admin/reports/MeetPointsDashboard/MeetPointsHelp';
import PointsVisibility from 'components/admin/reports/MeetPointsDashboard/PointsVisibility';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import { exportMeetPointsToFile, setMeetTeamTotalsVisibility } from 'api/reportsApi';
import { getMeetSummary } from 'api/clubApi';
import { getUserPermissionLevel } from 'services/auth/tokenService';
import { UserPermissionLevel } from 'constants/auth';
import { clubFeaturesOf, clubForId } from 'utils/clubFeatures';
import { exportAndDownloadFile } from 'utils/fileDownload';
import { pointsPageControls } from 'utils/meetPoints';
import { teamWordsOf } from 'utils/teamLabel';
import { convertDateTime } from 'utils/time';
import routes from 'routes';

const MeetPointsDashboardPage = () => {
  const navigate = useNavigate();
  const { clubId, meetId } = useParams();
  const [reportData, setReportData] = useState(null);
  const [meet, setMeet] = useState(null);
  const [savingVisibility, setSavingVisibility] = useState(false);

  // What the page offers (points slice 4b): an Admin exports it and, for a school, chooses who else sees
  // it; a school's members see it read only once released.
  const club = useSelector((state) => clubForId(state.user, clubId));
  const isAdmin = Number(getUserPermissionLevel()) >= UserPermissionLevel.Admin;
  const controls = pointsPageControls(isAdmin, clubFeaturesOf(club).teams);

  const fetchMeet = useCallback(async () => {
    try {
      const response = await getMeetSummary(clubId, meetId);
      setMeet(response.data);
    } catch (err) {
      console.error('[MeetPointsDashboard] Fetch meet error: ', err);
    }
  }, [clubId, meetId]);

  useEffect(() => {
    if (clubId && meetId) fetchMeet();
  }, [fetchMeet, clubId, meetId]);

  const handleBackToMeet = () => {
    navigate(routes.admin.showMeet.url(clubId, meetId));
  };

  const handleExportToFile = async () => {
    await exportAndDownloadFile(
      exportMeetPointsToFile,
      [meetId, reportData?.includeZeroPoints],
      '',
      'The meet points report was successfully exported',
      'Fetching meet points data from the server...'
    );
  };

  const isExportButtonDisabled = !reportData || !reportData.hasData;

  // Who sees this page: one write, which deletes the server's saved copies, then the points asked again so
  // the dropdown shows what was saved.
  const handleVisibilityChange = async (visibility) => {
    setSavingVisibility(true);
    try {
      await setMeetTeamTotalsVisibility(meetId, visibility);
      await reportData?.refresh?.();
    } catch (err) {
      console.error('[MeetPointsDashboard] Save who sees this page error: ', err);
      toast.error(err.response?.data?.message || 'Could not save who sees this page.');
    } finally {
      setSavingVisibility(false);
    }
  };

  return (
    <>
      <MetaTags title="SplitFast | Meet Points" />
      <AdminTablePageLayout>
        <MeetPageHeader
          title="Meet Points"
          meetName={meet?.meetName}
          meetDate={meet ? convertDateTime(meet.meetDate).date : undefined}
          onBackToMeet={handleBackToMeet}
          titleBadge={
            // Who sees this page: a badge beside the title, for a school's Admins (points slice 4b).
            controls.whoSeesThisPage ? (
              <PointsVisibility
                visibility={reportData?.teamTotalsVisibility}
                onChange={handleVisibilityChange}
                saving={savingVisibility}
                teamWords={teamWordsOf(club)}
              />
            ) : null
          }
        >
          {/* Beside Export, to its left (Doug, 2026-10-24, H9: it was in the athletes table's search row).
              A member's only once the points have come: none while told they are not released. */}
          {(isAdmin || (reportData && !reportData.notReleased)) && (
            <MeetPointsHelp explanation={reportData?.explanation ?? []} />
          )}
          {controls.exportFile && (
            <CustomNewButton
              variant="info"
              onClick={handleExportToFile}
              disabled={isExportButtonDisabled}
            >
              Export
            </CustomNewButton>
          )}
        </MeetPageHeader>
        {!meetId ? (
          <div style={{ minHeight: '100vh' }} />
        ) : (
          <MeetPointsDashboard meetId={meetId} onDataChange={setReportData} />
        )}
      </AdminTablePageLayout>
    </>
  );
};

export default MeetPointsDashboardPage;
